import { 
  Stack, 
  Card, 
  Box, 
  Typography, 
  Chip, 
  Grid, 
  Paper,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import { useList } from "src/hooks/useFetch.hook";
import { BaseTable } from "../BaseTable/BaseTable";
import { useSelector, useDispatch } from "react-redux";
import { tagListColumns } from "./Tags.constants";
import Page from "../Page";
import { useAccess } from "src/hooks/useAccess.hook";
import { FEATURE } from "src/util/enums";
import TagAttachChargerModal from "./TagAttachChargerModal";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { yearFirstDateTimeFormat, yearFirstDateTimeFormatUTC, yearFirstDateFormatUTC } from "src/utils/formatTime";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.customShadows.z8,
  }
}));

export const TagList = () => {
  const dispatch = useDispatch();
  const navigate= useNavigate()
  const hasCreateAccess = useAccess(FEATURE.TAG, "C");
  const isReadOnly = useAccess(FEATURE.TAG, "R");
  const { data: tags, isFetching } = useList("tags");
  const { open, data } = useSelector(({ dialog }) => dialog);
  const handleClose = () => dispatch(dialogSlice.actions.setDialog(false));
  const [tableData, setTableData] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [filteredCount, setFilteredCount] = useState(0);
  
  const rowOrCellClickRedirect = ({ id }) => {
    if(isReadOnly) return;
    navigate(`/tag/${id}`);
  };

  // Calculate statistics from current table data (filtered data)
  const currentData = tableData || tags || [];
  const totalTags = currentData.length;
  
  // Calculate expired tags (tags where expirydate is in the past)
  const expiredTags = currentData.filter(tag => {
    if (!tag.expirydate) return false;
    const expiryDate = new Date(tag.expirydate);
    return !isNaN(expiryDate.getTime()) && expiryDate < new Date();
  })?.length || 0;
  
  // Helper function to check if a tag is expired
  const isTagExpired = (tag) => {
    if (!tag.expirydate) return false;
    const expiryDate = new Date(tag.expirydate);
    return !isNaN(expiryDate.getTime()) && expiryDate < new Date();
  };
  
  // Calculate blocked tags (not expired)
  const blockedTags = currentData.filter(tag => {
    const isExpired = isTagExpired(tag);
    const isBlocked = tag.blocked === "1" || tag.blocked === 1 || tag.blocked === true;
    return !isExpired && isBlocked;
  })?.length || 0;
  
  // Calculate in-transaction tags (not expired and not blocked)
  const inTransactionTags = currentData.filter(tag => {
    const isExpired = isTagExpired(tag);
    const isBlocked = tag.blocked === "1" || tag.blocked === 1 || tag.blocked === true;
    const inTransaction = tag.in_transaction === "1" || tag.in_transaction === 1 || tag.in_transaction === true;
    return !isExpired && !isBlocked && inTransaction;
  })?.length || 0;
  
  // Calculate active tags (not expired, not blocked, not in transaction)
  const activeTags = currentData.filter(tag => {
    const isExpired = isTagExpired(tag);
    const isBlocked = tag.blocked === "1" || tag.blocked === 1 || tag.blocked === true;
    const inTransaction = tag.in_transaction === "1" || tag.in_transaction === 1 || tag.in_transaction === true;
    return !isExpired && !isBlocked && !inTransaction;
  })?.length || 0;

  useEffect(() => {
    if (!isFetching && tags){
      let data = [...tags];
      setTableData(data);
      setFilteredCount(data.length);
    }
  }, [isFetching, tags]);

  const handleSearching = (event) => {
    const dataToSearchFrom = tags || [];
    const searchValue = event.target.value.trim().toString().toLowerCase();
    setSearchValue(searchValue);
    
    if (searchValue) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "tag",
            "parenttag",
            "expirydate",
            "in_transaction",
            "blocked",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) =>
              key === "expirydate"
                ? yearFirstDateFormatUTC(value).toString()
                : value ? value.toString().toLowerCase() : ""
            );
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchValue)
          );
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, currentObj];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
      setFilteredCount(filteredDataArr.length);
    } else {
      setTableData([...(tags || [])]);
      setFilteredCount(tags?.length || 0);
    }
  };
  
  return (
    <Page
      {...(hasCreateAccess && { path: `/tag/new` })}
      actionButtonText="Add New RFID Tag"
    >
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* <Typography variant="h4" sx={{ 
          fontWeight: 600, 
          color: (theme) => theme.palette.primary.main,
          mb: 1
        }}>
          RFID Tag Management
        </Typography> */}
        <Typography variant="body1" sx={{ 
          color: (theme) => theme.palette.text.secondary,
          mb: 3
        }}>
          Manage and monitor RFID Tags
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Paper sx={{ 
              p: 1.5, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.primary.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{
                  p: 0.8,
                  borderRadius: 1.2,
                  background: (theme) => theme.palette.primary.lighter,
                  mr: 1
                }}>
                  <Icon icon="eva:credit-card-outline" width={16} height={16} style={{ 
                    color: (theme) => theme.palette.primary.main 
                  }} />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Total Tags
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.primary.main 
              }}>
                {totalTags}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Paper sx={{ 
              p: 1.5, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.success.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{
                  p: 0.8,
                  borderRadius: 1.2,
                  background: (theme) => theme.palette.success.lighter,
                  mr: 1
                }}>
                  <Icon icon="eva:checkmark-circle-outline" width={16} height={16} style={{ 
                    color: (theme) => theme.palette.success.main 
                  }} />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Active Tags
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.success.main 
              }}>
                {activeTags}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Paper sx={{ 
              p: 1.5, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.error.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{
                  p: 0.8,
                  borderRadius: 1.2,
                  background: (theme) => theme.palette.error.lighter,
                  mr: 1
                }}>
                  <Icon icon="eva:close-circle-outline" width={16} height={16} style={{ 
                    color: (theme) => theme.palette.error.main 
                  }} />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Blocked Tags
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.error.main 
              }}>
                {blockedTags}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Paper sx={{ 
              p: 1.5, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.warning.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{
                  p: 0.8,
                  borderRadius: 1.2,
                  background: (theme) => theme.palette.warning.lighter,
                  mr: 1
                }}>
                  <Icon icon="eva:flash-outline" width={16} height={16} style={{ 
                    color: (theme) => theme.palette.warning.main 
                  }} />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  In Transaction
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.warning.main 
              }}>
                {inTransactionTags}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Paper sx={{ 
              p: 1.5, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.grey[500],
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{
                  p: 0.8,
                  borderRadius: 1.2,
                  background: (theme) => theme.palette.grey[100],
                  mr: 1
                }}>
                  <Icon icon="eva:clock-outline" width={16} height={16} style={{ 
                    color: (theme) => theme.palette.grey[600] 
                  }} />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Expired Tags
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.grey[600] 
              }}>
                {expiredTags}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Header Section */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            RFID Tags
          </Typography>
          {searchValue && (
            <Chip
              label={`${filteredCount} of ${tags?.length || 0} tags`}
              color="primary"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
      </Stack>
      
      {/* Combined Search and Table Card */}
      <StyledListCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CommonSearchbar
              placeholder="Search by tag ID, parent group, expiry date, or status..."
              searchFunc={(e) => handleSearching(e)}
            />
          </Box>
          <Tooltip title="Refresh data">
            <span>
              <IconButton
                onClick={() => window.location.reload()}
                disabled={isFetching}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Icon icon={refreshFill} width={20} height={20} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <BaseTable
          rows={tableData ?? []}
          isLoading={isFetching}
          columns={tagListColumns(isReadOnly)}
          showExportCsvBtn={true}
          csvMorphemes="auth_code"
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          pageSize={10}
          isReadOnly={isReadOnly}
          csvColumns={tagListColumns(isReadOnly)}
          height={Math.max(200, (tableData?.length || 0) * 52 + 100)}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-main': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-row': {
              height: '52px !important',
            },
            '& .MuiDataGrid-cell': {
              height: '52px !important',
            }
          }}
        />
      </StyledListCard>
      
      <TagAttachChargerModal open={open} data={data} onClose={handleClose} />
    </Page>
  );
};
